import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { PetProfile, UserProfile } from "../../types";
import axiosInstance from "../../utils/axiosInstance";
import { translate } from "../../utils/localisation";
import useMediaQuery from "@mui/material/useMediaQuery";

/** -------- Utils -------- */

const toStartCase = (k: string) =>
  k
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());

/** Clickable keys that deep-link to an external system */
const CLICK_INDICATORS = ["pack_subscription_id"] as const;
type ClickableKey = (typeof CLICK_INDICATORS)[number];

/** -------- Reusable responsive key-value renderer -------- */

type KVProps = {
  data: Record<string, unknown> | undefined | null;
  onClick?: (
    event: React.MouseEvent<unknown>,
    key: string,
    value: string
  ) => void;
  clickableKeys?: string[];
  title?: string;
  maxHeight?: number | string;
  dense?: boolean;
  /** Optional actions to render in the header area */
  headerAction?: React.ReactNode;
  /** Loading skeleton toggle */
  loading?: boolean;
};

function ResponsiveKVTable({
  data,
  onClick,
  clickableKeys = [],
  title,
  maxHeight = 405,
  dense = true,
  headerAction,
  loading = false,
}: KVProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const entries = useMemo(
    () =>
      data
        ? Object.entries(data).filter(
            ([, v]) => v !== undefined && v !== null && v !== ""
          )
        : [],
    [data]
  );

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderRadius: 3,
        boxShadow: t => `0 1px 2px ${alpha(t.palette.common.black, 0.04)}`,
      }}
    >
      {title && (
        <CardHeader
          titleTypographyProps={{ variant: "h6" }}
          title={title}
          action={headerAction}
          sx={{
            bgcolor: t => alpha(t.palette.primary.main, 0.04),
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
          }}
        />
      )}

      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} height={32} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : isMobile ? (
          /* ---- Mobile: list-style key/value cards ---- */
          <List sx={{ py: 0, maxHeight, overflow: "auto" }}>
            {entries.map(([key, raw]) => {
              const value = String(raw);
              const isClickable = clickableKeys.includes(key) && !!value;
              return (
                <ListItem
                  key={key}
                  divider
                  sx={{
                    alignItems: "flex-start",
                    "&:nth-of-type(odd)": {
                      bgcolor: t => alpha(t.palette.action.hover, 0.3),
                    },
                    cursor: isClickable ? "pointer" : "default",
                  }}
                  onClick={
                    isClickable && onClick
                      ? e => onClick(e, key, value)
                      : undefined
                  }
                  secondaryAction={
                    isClickable ? (
                      <Tooltip
                        title={translate("open-in-new") || "Open in new tab"}
                      >
                        <IconButton edge="end" size="small">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {toStartCase(key)}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {value}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          /* ---- Desktop: table with sticky header ---- */
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight,
              border: 0,
              boxShadow: "none",
            }}
          >
            <Table size={dense ? "small" : "medium"} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {translate("attribute")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {translate("value")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(([key, raw]) => {
                  const value = String(raw);
                  const isClickable = clickableKeys.includes(key) && !!value;

                  return (
                    <TableRow
                      key={key}
                      hover={isClickable}
                      onClick={
                        isClickable && onClick
                          ? e => onClick(e, key, value)
                          : undefined
                      }
                      sx={{
                        "&:nth-of-type(odd)": {
                          bgcolor: t => alpha(t.palette.action.hover, 0.25),
                        },
                        cursor: isClickable ? "pointer" : "default",
                      }}
                    >
                      <TableCell width="35%">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          {toStartCase(key)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                              flex: 1,
                            }}
                            title={value}
                          >
                            {value}
                          </Typography>
                          {isClickable && <OpenInNewIcon fontSize="small" />}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

/** -------- Page Component -------- */

export function UserProfileView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pets, setPets] = useState<PetProfile[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [subData, setSubData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmPet, setConfirmPet] = useState<PetProfile | null>(null);

  const { userId } = useParams();

  useEffect(() => {
    let mounted = true;

    async function fetchExtraDetails() {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/admin/user-profile/${userId}`);

        if (!mounted) return;

        setPets(res.data.details?.pets ?? []);
        setSubData(res.data.details?.stripe_customer_info?.[0] ?? null);
        setUserProfile(res.data.userDetails);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchExtraDetails();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const handleClick = (
    event: React.MouseEvent<unknown>,
    key: string,
    value: string
  ) => {
    switch (key as ClickableKey) {
      case "pack_subscription_id":
        // open in new tab instead of hijacking the whole app
        window.open(
          `https://dashboard.stripe.com/test/subscriptions/${value}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      default:
        break;
    }
  };

  const requestDelete = (pet: PetProfile) => setConfirmPet(pet);

  const handleDelete = async () => {
    if (!confirmPet) return;
    try {
      await axiosInstance.delete(`/admin/pet-profile/${confirmPet.id}`);
      // Optimistic local update
      setPets(list =>
        list.map(p => (p.id === confirmPet.id ? { ...p, is_active: false } : p))
      );
    } finally {
      setConfirmPet(null);
    }
  };

  return (
    <>
      <Typography
        variant={isMobile ? "h4" : "h3"}
        component="h2"
        sx={{ mb: 1 }}
      >
        {translate("user-profiles-selected-screen.title-text")}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ResponsiveKVTable
            title={translate([
              "user-profiles-selected-screen.user-table-caption-text",
              {
                var: `${userProfile?.first_name || ""} ${
                  userProfile?.last_name || ""
                }`,
              },
            ])}
            data={userProfile as unknown as Record<string, unknown>}
            clickableKeys={[]}
            onClick={handleClick}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ResponsiveKVTable
            title={translate(
              "user-profiles-selected-screen.subscription-table-caption-text"
            )}
            data={subData as Record<string, unknown>}
            clickableKeys={CLICK_INDICATORS as unknown as string[]}
            onClick={handleClick}
            loading={loading}
          />
        </Grid>

        {pets?.length
          ? pets.map(pet => (
              <Grid item xs={12} md={6} key={pet.id}>
                <ResponsiveKVTable
                  title={translate([
                    "user-profiles-selected-screen.pet-table-caption-text",
                    { var: pet?.name || "" },
                  ])}
                  data={pet as unknown as Record<string, unknown>}
                  clickableKeys={CLICK_INDICATORS as unknown as string[]}
                  onClick={handleClick}
                  headerAction={
                    pet.is_active ? (
                      <Tooltip title={translate("deactivate") || "Deactivate"}>
                        <IconButton
                          size="small"
                          onClick={() => requestDelete(pet)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button size="small" variant="outlined" disabled>
                        {translate("inactive") || "Inactive"}
                      </Button>
                    )
                  }
                />
              </Grid>
            ))
          : !loading && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body1">
                      {translate("no-pets-found") ||
                        "No pets found for this profile."}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
      </Grid>

      {/* Confirm dialog for pet deactivation */}
      <Dialog open={!!confirmPet} onClose={() => setConfirmPet(null)}>
        <DialogTitle>
          {translate("confirm-deactivation") || "Confirm deactivation"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {translate("are-you-sure-deactivate") ||
              `Are you sure you want to deactivate ${confirmPet?.name}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmPet(null)}>
            {translate("cancel") || "Cancel"}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {translate("deactivate") || "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
